import { useState } from 'react';
import { ObjectPreset } from '@/types/simulation';
import { useTheme } from '@/contexts/ThemeContext';

interface Props {
  presets: ObjectPreset[];
  hasObjects: boolean;
  onSave: (name: string) => void;
  onLoad: (id: string) => void;
  onDelete: (id: string) => void;
}

function formatDate(ts: number): string {
  const d = new Date(ts);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd} ${hh}:${min}`;
}

export default function PresetPanel({ presets, hasObjects, onSave, onLoad, onDelete }: Props) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [nameInput, setNameInput] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [loadedId, setLoadedId] = useState<string | null>(null);

  const textMuted     = isDark ? 'text-white/35' : 'text-gray-500';
  const textSecondary = isDark ? 'text-white/60' : 'text-gray-600';
  const textPrimary   = isDark ? 'text-white'    : 'text-gray-900';
  const textDim       = isDark ? 'text-white/18' : 'text-gray-300';
  const borderSubtle  = isDark ? 'border-white/6' : 'border-black/6';

  const handleSave = () => {
    const name = nameInput.trim() || `プリセット ${presets.length + 1}`;
    onSave(name);
    setNameInput('');
  };

  const handleLoad = (id: string) => {
    onLoad(id);
    setLoadedId(id);
    setTimeout(() => setLoadedId(null), 1500);
  };

  return (
    <div className="space-y-3 animate-float-in">
      <h2 className="section-label flex items-center gap-2">
        <div className="w-4 h-4 flex items-center justify-center">
          <i className={`ri-bookmark-line ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
        </div>
        プリセット
      </h2>

      {/* Save area */}
      <div className="glass rounded-xl p-3 space-y-2">
        <p className={`text-xs ${textMuted}`}>現在の物体配置を保存</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && hasObjects && handleSave()}
            placeholder="プリセット名（省略可）"
            className={`glass-input flex-1 text-xs px-3 py-2 rounded-lg outline-none ${textPrimary} placeholder:${textDim}`}
            maxLength={30}
          />
          <button
            onClick={handleSave}
            disabled={!hasObjects}
            className={`ripple-btn px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
              hasObjects
                ? `${isDark ? 'bg-amber-500/30 border border-amber-400/40 text-amber-300 hover:bg-amber-500/50' : 'bg-amber-100 border border-amber-300 text-amber-700 hover:bg-amber-200'}`
                : `glass-btn ${textDim} cursor-not-allowed opacity-50`
            }`}
            title={hasObjects ? '保存' : '物体を追加してから保存'}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-save-line" />
            </div>
          </button>
        </div>
        {!hasObjects && (
          <p className={`text-xs ${textDim}`}>物体タブで物体を追加してから保存できます</p>
        )}
      </div>

      {/* Preset list */}
      {presets.length === 0 ? (
        <div className="glass rounded-xl py-7 text-center">
          <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
            <i className={`ri-bookmark-line text-2xl ${textDim}`} />
          </div>
          <p className={`text-xs ${textDim} leading-relaxed`}>
            保存したプリセットがありません
          </p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {presets.map((preset) => (
            <div
              key={preset.id}
              className={`glass rounded-xl overflow-hidden transition-all duration-200 ${
                loadedId === preset.id ? (isDark ? 'ring-1 ring-emerald-400/50' : 'ring-1 ring-emerald-400') : ''
              }`}
            >
              <div className="flex items-center gap-2 px-3 py-2.5">
                {/* Color dots */}
                <div className="flex gap-0.5 flex-shrink-0">
                  {preset.objects.slice(0, 4).map((o, i) => (
                    <span
                      key={i}
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: o.color }}
                    />
                  ))}
                  {preset.objects.length > 4 && (
                    <span className={`text-xs ${textDim}`}>+{preset.objects.length - 4}</span>
                  )}
                </div>

                {/* Name + meta */}
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${textSecondary} truncate`}>{preset.name}</p>
                  <p className={`text-xs ${textDim}`}>
                    {preset.objects.length}個 · {formatDate(preset.createdAt)}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {loadedId === preset.id ? (
                    <span className={`text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'} flex items-center gap-1`}>
                      <i className="ri-check-line" /> 読込済
                    </span>
                  ) : (
                    <button
                      onClick={() => handleLoad(preset.id)}
                      className={`ripple-btn glass-btn px-2.5 py-1 rounded-lg text-xs cursor-pointer whitespace-nowrap transition-all ${isDark ? 'text-cyan-300 hover:text-white' : 'text-sky-600 hover:text-gray-900'}`}
                      title="このプリセットを読み込む"
                    >
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-download-line" />
                      </div>
                    </button>
                  )}

                  {confirmDeleteId === preset.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => { onDelete(preset.id); setConfirmDeleteId(null); }}
                        className="ripple-btn px-2 py-1 rounded-lg text-xs text-red-400 glass-btn cursor-pointer whitespace-nowrap"
                      >
                        削除
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className={`ripple-btn px-2 py-1 rounded-lg text-xs ${textDim} glass-btn cursor-pointer whitespace-nowrap`}
                      >
                        戻る
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteId(preset.id)}
                      className={`ripple-btn w-6 h-6 flex items-center justify-center rounded-lg glass-btn cursor-pointer transition-all ${textDim} hover:text-red-400`}
                      title="削除"
                    >
                      <i className="ri-delete-bin-line text-xs" />
                    </button>
                  )}
                </div>
              </div>

              {/* Object summary bar */}
              <div className={`border-t ${borderSubtle} px-3 py-1.5 flex gap-3 overflow-x-auto`} style={{ scrollbarWidth: 'none' }}>
                {preset.objects.map((o, i) => (
                  <div key={i} className="flex items-center gap-1 flex-shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: o.color }} />
                    <span className={`text-xs font-mono ${textDim}`}>
                      ({o.x.toFixed(1)},{o.y.toFixed(1)}) {Math.sqrt(o.vx**2+o.vy**2).toFixed(1)}m/s
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
